import { useCallback, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MoveHorizontal } from "lucide-react";
import { MaskLines } from "./Motion";
import { EASE } from "@/lib/site";

const C = (n) => `/chantiers/${n}.webp`;
const PROJECTS = [
  { title: "Grand salon à moulures", type: "Rénovation", work: "Reprise des moulures, enduits, peinture murs & plafonds", beforeLabel: "PENDANT", before: C("salon-avant"), after: C("salon-apres") },
  { title: "Pièce complète — murs, plafond & sol", type: "Rénovation", work: "Dépose du papier peint, enduit, peinture, pose de sol stratifié", before: C("piece-avant"), after: C("piece-apres") },
  { title: "Devanture de boutique", type: "Extérieur", work: "Préparation des boiseries, rebouchage, laque noire satinée", beforeLabel: "PENDANT", before: C("vitrine-avant"), after: C("vitrine-apres"), wide: true },
  { title: "Salle de bain sous combles", type: "Rénovation", work: "Peinture murs & plafond, poutre, carrelage sol & douche", before: C("sdb-avant"), after: C("sdb-apres"), tall: true },
  { title: "Chambre — mur bordeaux", type: "Construction neuve", work: "Bandes, enduit de finition, peinture couleur", before: C("chambre-avant"), after: C("chambre-apres") },
  { title: "Cage d'escalier", type: "Rénovation", work: "Lissage du crépi, enduit, peinture murs & plafond", before: C("escalier-avant"), after: C("escalier-apres"), tall: true },
  { title: "Séjour — mur terracotta", type: "Construction neuve", work: "Préparation des plaques, peinture blanche & couleur", before: C("sejour-avant"), after: C("sejour-apres") },
  { title: "Montée d'escalier", type: "Rénovation", work: "Lissage du crépi, finition mate blanche", before: C("cage-avant"), after: C("cage-apres"), tall: true },
  { title: "Pièce ancienne", type: "Rénovation", work: "Décapage, reprise des murs abîmés, peinture", beforeLabel: "PENDANT", before: C("mur-avant"), after: C("mur-apres") },
  { title: "Chambre à poutres", type: "Rénovation", work: "Bandes, enduit de lissage, peinture murs & plafond, poutres conservées", before: C("poutres-avant"), after: C("poutres-apres"), wide: true },
  { title: "Chambre sous combles", type: "Rénovation", work: "Dépose du papier peint, enduit, peinture blanche mate", before: C("combles-avant"), after: C("combles-apres"), wide: true },
  { title: "Pièce à poutres — portes laquées", type: "Rénovation", work: "Enduit, peinture murs & plafond, laquage des portes", before: C("portes-avant"), after: C("portes-apres"), wide: true },
  { title: "Chambre — parquet & poutres", type: "Rénovation", work: "Préparation des murs, peinture, plinthes", before: C("rose-avant"), after: C("rose-apres"), wide: true },
  { title: "Salle d'eau sous combles", type: "Rénovation", work: "Peinture plafond & poutre, carrelage sol et douche", before: C("sdb2-avant"), after: C("sdb2-apres"), tall: true },
  { title: "Allée & murets extérieurs", type: "Extérieur", work: "Nettoyage, traitement, peinture façade des murets", box: true, before: C("allee-avant"), after: C("allee-apres") },
  { title: "Murets de descente de garage", type: "Extérieur", work: "Reprise des épaufrures, peinture extérieure", box: true, before: C("muret-avant"), after: C("muret-apres") },
  { title: "Fenêtre extérieure", type: "Extérieur", work: "Ponçage, peinture de la menuiserie et de l'appui", before: C("fenetre-avant"), after: C("fenetre-apres"), tall: true },
  { title: "Appartement — rénovation complète", type: "Rénovation", work: "Traitement des murs, enduit, peinture, pose de sol", beforeLabel: "PENDANT", box: true, before: C("appart-avant"), after: C("appart-apres") },
  { title: "Séjour — plafond & mur anthracite", type: "Rénovation", work: "Plafond blanc mat, mur d'accent anthracite", before: C("plafond-avant"), after: C("plafond-apres"), tall: true },
  { title: "Porte-fenêtre — embrasure", type: "Rénovation", work: "Création de l'embrasure, enduit, peinture", box: true, before: C("porte-avant"), after: C("porte-apres") },
  { title: "Véranda — extérieur", type: "Extérieur", work: "Décapage, ponçage et peinture des menuiseries", beforeLabel: "PENDANT", before: C("veranda-ext-avant"), after: C("veranda-ext-apres"), wide: true },
  { title: "Véranda — intérieur", type: "Rénovation", work: "Bandes, enduit, peinture plafond, murs & boiseries", before: C("veranda-int-avant"), after: C("veranda-int-apres"), tall: true },
  { title: "Bureau — parquet & ciel", type: "Rénovation", work: "Dépose du papier peint, enduit, peinture blanche", before: C("bureau-avant"), after: C("bureau-apres"), tall: true },
  { title: "WC suspendu", type: "Construction neuve", work: "Bandes, enduit, peinture bleu-gris & plafond", before: C("wc-avant"), after: C("wc-apres"), tall: true },
  { title: "Chambre — mur bleu sauge", type: "Construction neuve", work: "Préparation des plaques, peinture blanche & couleur", before: C("chambre-bleue-avant"), after: C("chambre-bleue-apres"), tall: true },
];

const Compare = ({ p }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const [drag, setDrag] = useState(false);
  const fit = p.tall ? "object-contain" : "object-cover";
  const update = useCallback((x) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(100, Math.max(0, ((x - r.left) / r.width) * 100)));
  }, []);
  return (
    <div
      ref={ref}
      data-testid="before-after-compare"
      className="absolute inset-0 cursor-ew-resize select-none touch-pan-y"
      onPointerDown={(e) => { setDrag(true); update(e.clientX); e.currentTarget.setPointerCapture(e.pointerId); }}
      onPointerMove={(e) => drag && update(e.clientX)}
      onPointerUp={() => setDrag(false)}
    >
      {p.tall && <img src={p.after} alt="" aria-hidden className="absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-2xl" draggable={false} />}
      <img src={p.before} alt={`${p.title} avant travaux`} className={`absolute inset-0 h-full w-full ${fit}`} draggable={false} />
      <img
        src={p.after}
        alt={`${p.title} après travaux`}
        className={`absolute inset-0 h-full w-full ${fit}`}
        style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
        draggable={false}
      />
      <span className="absolute left-4 top-4 bg-ink/80 px-3 py-1.5 font-mono text-[11px] tracking-[0.2em] text-paper backdrop-blur">{p.beforeLabel || "AVANT"}</span>
      <span className="absolute right-4 top-4 bg-gold px-3 py-1.5 font-mono text-[11px] tracking-[0.2em] text-ink">APRÈS</span>
      <div className="absolute inset-y-0 w-px bg-paper" style={{ left: `${pos}%` }}>
        <span className={`absolute top-1/2 left-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-paper text-ink shadow-2xl transition-transform duration-300 ${drag ? "scale-90" : "scale-100"}`} data-testid="before-after-handle">
          <MoveHorizontal className="h-5 w-5" />
        </span>
      </div>
      <input
        type="range" min="0" max="100" value={pos} onChange={(e) => setPos(+e.target.value)}
        aria-label="Comparer avant et après" data-testid="before-after-range"
        className="sr-only"
      />
    </div>
  );
};

export const BeforeAfter = () => {
  const [i, setI] = useState(0);
  const p = PROJECTS[i];
  const go = (d) => setI((v) => (v + d + PROJECTS.length) % PROJECTS.length);
  return (
    <section id="realisations" data-testid="before-after-carousel" className="grain relative bg-ink px-5 py-24 text-paper sm:px-8 lg:py-32">
      <div className="mx-auto max-w-[1400px]">
        <div className="mb-14 grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow text-gold mb-6">Galerie avant / après</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              <MaskLines lines={["La preuve par", <em key="e" className="text-gold">le résultat.</em>]} />
            </h2>
          </div>
          <p className="lg:col-span-5 text-paper/80">Glissez le curseur pour comparer. De vrais chantiers réalisés par notre équipe, photographiés avant ou pendant les travaux, puis à la réception.</p>
        </div>

        <div className="grid gap-8 lg:grid-cols-12">
          <div className={`relative overflow-hidden bg-ink-soft lg:col-span-9 ${p.tall ? "aspect-[3/4] lg:aspect-[16/10]" : p.wide ? "aspect-[2/1] lg:aspect-[12/5]" : p.box ? "aspect-[4/3] lg:aspect-[5/4]" : "aspect-[4/3] lg:aspect-[16/10]"}`}>
            <AnimatePresence initial={false}>
              <motion.div
                key={i}
                className="absolute inset-0"
                initial={{ clipPath: "inset(0 0 0 100%)" }}
                animate={{ clipPath: "inset(0 0 0 0%)" }}
                exit={{ opacity: 0.6 }}
                transition={{ duration: 1.1, ease: EASE }}
              >
                <Compare p={p} />
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex flex-col justify-between gap-8 lg:col-span-3">
            <div>
              <div className="font-mono text-sm text-gold" data-testid="before-after-counter">{String(i + 1).padStart(2, "0")} / {String(PROJECTS.length).padStart(2, "0")}</div>
              <h3 className="mt-4 font-display text-4xl leading-tight" data-testid="before-after-title">{p.title}</h3>
              <dl className="mt-6 space-y-3 text-sm">
                {[["Type", p.type], ["Travaux", p.work]].map(([k, v]) => (
                  <div key={k} className="flex gap-4 border-b border-ink-line pb-3">
                    <dt className="w-16 shrink-0 text-paper/60">{k}</dt><dd className="text-paper/95">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div>
              <div className="flex gap-2 mb-6">
                {PROJECTS.map((pr, k) => (
                  <button key={k} onClick={() => setI(k)} data-testid={`before-after-dot-${k}`} aria-label={pr.title}
                    className="h-1 flex-1 bg-ink-line overflow-hidden">
                    <span className={`block h-full bg-gold transition-transform duration-700 origin-left ${k === i ? "scale-x-100" : "scale-x-0"}`} />
                  </button>
                ))}
              </div>
              <div className="flex gap-3">
                <button onClick={() => go(-1)} data-testid="before-after-prev" aria-label="Précédent" className="flex h-14 w-14 items-center justify-center rounded-full border border-paper/30 transition-colors duration-300 hover:bg-paper hover:text-ink"><ArrowLeft className="h-5 w-5" /></button>
                <button onClick={() => go(1)} data-testid="before-after-next" aria-label="Suivant" className="flex h-14 w-14 items-center justify-center rounded-full bg-gold text-ink transition-colors duration-300 hover:bg-paper"><ArrowRight className="h-5 w-5" /></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
